import { EnvironmentProviders, makeEnvironmentProviders, isDevMode, inject, provideAppInitializer } from '@angular/core';
import { provideStore, provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore, NavigationActionTiming } from '@ngrx/router-store';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';

import { reducers, metaReducers } from './NGRX/core.state';
import { CustomSerializer } from './NGRX/router/custom-serializer';
import { LocalStorageService } from './NGRX/meta-reducers/local-storage.service';
import { TrackingMiddleware } from './middlewares/tracking/tracking.middleware';
import { authInterceptor } from './services/client/auth.interceptor';
import { provideConfig } from './services/config/config.module';
import { provideGuards } from './services/guards/guards.module';
import { provideClient } from './services/client/client.module';
import { provideCoreTranslation } from './services/translation/translation.module';
import { provideWebsockets } from './services/websockets/websockets.module';
import { provideSelections } from './services/selections/selections.module';
import { provideStake } from './services/stake/stake.module';
import { provideUtils } from './services/utils/utils.module';
import { provideSentry } from './services/sentry/sentry.module';
import { provideGeolocation } from './services/geolocation/geolocation.module';
// Import all effects
import { MicrositePoolsEffects } from './NGRX/pool/pool.effects';
import { MicrositeWebsocketsEffects } from './NGRX/coreWebsockets/coreWebsockets.effects';
import { TrackingEffects } from './NGRX/coreTracking/coreTracking.effects';
import { MicrositeSessionsEffects } from './NGRX/coreAuth/coreAuth.effects';
import { LeaderboardRelatedEffects } from './NGRX/manager/leaderboard-related/leaderboard-related.effects';
import { ManagerOtherEffects } from './NGRX/manager/other/other-related.effects';
import { ManagerRelatedEffects } from './NGRX/manager/manager-related/manager-related.effects';
import { SyndicateSoloEffects } from './NGRX/syndicates/syndicate/syndicate-solo.effects';
import { SyndicatesListEffects } from './NGRX/syndicates/list/syndicates-list.effects';
import { SyndicateCommentaryEffects } from './NGRX/syndicates/comments/commentary.effects';
import { TicketsListEffects } from './NGRX/tickets/list/tickets-list.effects';
import { SoloTicketEffects } from './NGRX/tickets/solo/solo-tickets.effects';
import { RouterEffects } from './NGRX/router/router.effects';
import { ConfigurationEffects } from './NGRX/configuration/configuration.effects';
import { FreeplayEffects } from './NGRX/freeplay/freeplay.effects';
import { SportEventsEffects } from './NGRX/sport-events/sport-events.effects';

/**
 * Provides the complete cb-core infrastructure — the single, unified entry
 * point for the submodule. cb-core is unique and must always be initialized
 * first and completely, before any other module. Call this once, ahead of
 * everything else, in the app's standalone bootstrap.
 *
 * Includes:
 * - NgRx Store with reducers, meta-reducers, and runtime checks
 * - All 17 root effects
 * - StoreDevtools + router-store integration
 * - HTTP client + auth interceptor, guards, config, selections, stake,
 *   geolocation, client, websockets, utils, sentry, translation
 * - Tracking initialization
 *
 * Replaces the former CbfeCoreModule (NgModule), the separate
 * provideCbfeStore() function, and the incomplete provideCbfeServices() stub.
 *
 * @example
 * bootstrapApplication(AppComponent, {
 *   providers: [provideCbfeCore(), ...]
 * });
 */
export function provideCbfeCore(): EnvironmentProviders {
  return makeEnvironmentProviders([
    // NgRX Store with runtime checks
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionSerializability: false, // Allow non-serializable in actions (e.g., functions)
        strictStateSerializability: false, // Allow non-serializable in state (e.g., Date objects)
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true
      }
    }),

    // Stub 'groupTickets' feature state — cb-group-tickets isn't onboarded yet,
    // but libs/ui/group-tickets/gt-bridge.ts's createFeatureSelector('groupTickets')
    // expects the key to exist. Registering it here (rather than leaving it absent)
    // stops @ngrx/store's own dev-only "does not exist in the state" warning; once
    // cb-group-tickets is onboarded for real, its own reducer overwrites this stub.
    provideState('groupTickets', (state = {}) => state),

    // All 17 Root Effects (organized by domain)
    provideEffects(
      // Core Infrastructure
      MicrositePoolsEffects,
      MicrositeWebsocketsEffects,
      TrackingEffects,
      MicrositeSessionsEffects,
      RouterEffects,
      ConfigurationEffects,
      SportEventsEffects,
      FreeplayEffects,

      // Manager Domain
      LeaderboardRelatedEffects,
      ManagerOtherEffects,
      ManagerRelatedEffects,

      // Syndicates Domain
      SyndicateSoloEffects,
      SyndicatesListEffects,
      SyndicateCommentaryEffects,

      // Tickets Domain
      TicketsListEffects,
      SoloTicketEffects
    ),

    // Redux DevTools (development + production logging)
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      trace: isDevMode(),
      traceLimit: 75,
      connectInZone: true
    }),

    // Router Store Integration
    provideRouterStore({
      serializer: CustomSerializer,
      navigationActionTiming: NavigationActionTiming.PostActivation
    }),

    // Core services
    LocalStorageService,
    TrackingMiddleware,
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideGuards(),
    provideConfig(),
    provideSelections(),
    provideStake(),
    provideGeolocation(),
    provideClient(),
    provideWebsockets(),
    provideUtils(),
    provideSentry(),
    provideCoreTranslation(),

    // Tracking init — after Store + Config are already provided above
    provideAppInitializer(() => {
      inject(TrackingMiddleware).initTracking();
    })
  ]);
}
